import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { ClientLayout } from "../components/ClientLayout";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { clientApi } from "../services/clientApi.service";
import { getReturnPath, parseAddressFormOrigin } from "../utils/addressFormReturn";

interface AddressFormState {
  label: string;
  address: string;
  postalCode: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
  isDefault: boolean;
}

const EMPTY_FORM: AddressFormState = {
  label: "",
  address: "",
  postalCode: "",
  city: "",
  latitude: null,
  longitude: null,
  isDefault: false,
};

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: string; error?: { message?: string } } } })
    .response?.data;
  return data?.message ?? data?.error?.message ?? fallback;
}

/**
 * Création ou modification d'une adresse (page dédiée).
 * Redirection selon `?from=addresses` ou `?from=order`.
 */
export function ClientAddressFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { addressId } = useParams<{ addressId: string }>();
  const [searchParams] = useSearchParams();
  const origin = parseAddressFormOrigin(searchParams);
  const isEdit = Boolean(addressId);

  const [form, setForm] = useState<AddressFormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [addressFieldError, setAddressFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !addressId) return;

    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setLoadError(null);
      try {
        const addresses = await clientApi.listAddresses();
        if (cancelled) return;
        const addr = addresses.find((a) => a.id === addressId);
        if (!addr) {
          setLoadError("Adresse introuvable.");
          return;
        }
        setForm({
          label: addr.label ?? "",
          address: addr.address,
          postalCode: addr.postalCode ?? "",
          city: addr.city ?? "",
          latitude: addr.latitude,
          longitude: addr.longitude,
          isDefault: addr.isDefault,
        });
      } catch (err) {
        if (cancelled) return;
        setLoadError(extractApiErrorMessage(err, "Impossible de charger cette adresse."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [addressId, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    let cancelled = false;
    async function initDefault(): Promise<void> {
      try {
        const addresses = await clientApi.listAddresses();
        if (cancelled) return;
        if (addresses.length === 0) {
          setForm((f) => ({ ...f, isDefault: true }));
        }
      } catch {
        /* ignore */
      }
    }
    void initDefault();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  function handleBack(): void {
    navigate(getReturnPath(origin));
  }

  async function handleSubmit(): Promise<void> {
    setSaving(true);
    setSubmitError(null);
    setAddressFieldError(null);
    try {
      if (!form.address.trim()) {
        const message =
          "Saisissez la rue et le numéro (champ Adresse), pas seulement le code postal ou la ville.";
        setAddressFieldError(message);
        setSubmitError(message);
        return;
      }
      if (!form.postalCode.trim() && !form.city.trim()) {
        setSubmitError("Indiquez au minimum le code postal ou la ville.");
        return;
      }
      const payload = {
        label: form.label.trim() || null,
        address: form.address.trim(),
        postalCode: form.postalCode.trim() || null,
        city: form.city.trim() || null,
        latitude: form.latitude,
        longitude: form.longitude,
        isDefault: form.isDefault,
      };

      const saved = isEdit && addressId
        ? await clientApi.updateAddress(addressId, payload)
        : await clientApi.createAddress(payload);

      const returnPath = getReturnPath(origin);
      if (origin === "order") {
        navigate(returnPath, { replace: true, state: { selectedAddressId: saved.id } });
      } else {
        navigate(returnPath, { replace: true });
      }
    } catch (err) {
      const message = extractApiErrorMessage(err, "Impossible d'enregistrer cette adresse.");
      setSubmitError(message);
      if (/adresse/i.test(message)) {
        setAddressFieldError(message);
      }
    } finally {
      setSaving(false);
    }
  }

  const title = isEdit ? "Modifier l'adresse" : "Nouvelle adresse";

  return (
    <ClientLayout title={title}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={handleBack} aria-label="Retour">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>

        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              {submitError ? <Alert severity="error">{submitError}</Alert> : null}
              <Typography variant="caption" color="text.secondary">
                La zone de livraison est calculée automatiquement. Si votre adresse est hors zone,
                vous pourrez uniquement commander en retrait sur place.
              </Typography>
              <TextField
                fullWidth
                label="Libellé (ex. Domicile, Bureau)"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
              <AddressAutocomplete
                address={form.address}
                postalCode={form.postalCode}
                city={form.city}
                onAddressChange={({ address, postalCode, city, latitude, longitude }) => {
                  setAddressFieldError(null);
                  setForm((f) => ({
                    ...f,
                    address,
                    postalCode: postalCode ?? f.postalCode,
                    city: city ?? f.city,
                    latitude: latitude ?? null,
                    longitude: longitude ?? null,
                  }));
                }}
                label="Adresse"
                required
                error={!!addressFieldError}
                helperText={
                  addressFieldError ??
                  "Rue et numéro (sélectionnez une suggestion, la localisation ou saisissez l'adresse)."
                }
                fullWidth
                size="small"
                showLocateButton
                onLocateError={(message) => {
                  setAddressFieldError(message);
                  setSubmitError(message);
                }}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField
                  label="Code postal"
                  value={form.postalCode}
                  onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
                  size="small"
                  sx={{ width: 140 }}
                  required
                />
                <TextField
                  label="Ville"
                  value={form.city}
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  size="small"
                  sx={{ flex: 1, minWidth: 160 }}
                  required
                />
              </Stack>
              <Box
                component="label"
                sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
                />
                <Typography variant="body2">Définir comme adresse par défaut</Typography>
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={handleBack} disabled={saving}>
                  Annuler
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="secondary"
                  disabled={saving}
                >
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>
    </ClientLayout>
  );
}
